import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { FAQ_ITEMS } from "../utils/faqData";

const SITE_URL = "https://trainer-app.com";
const DEFAULT_IMAGE = `${SITE_URL}/logo512.png`;

const DEFAULT_SEO = {
  title: "Trainer App | Online Coaching Platform for Personal Trainers",
  description:
    "Trainer App is the personal training app for trainers and online fitness coaches. Build training programs, track client workouts, manage check-ins and payments, and grow your online coaching business.",
};

// Per-route SEO. `canonical` consolidates duplicate paths (e.g. /about-us ->
// /about) so each marketing page is indexed as a distinct, canonical URL
// instead of all pointing at the homepage.
const ROUTE_SEO = {
  "/": {
    canonical: "/",
    title: "Trainer App | Online Coaching Platform for Personal Trainers",
    description: DEFAULT_SEO.description,
  },
  "/about": {
    canonical: "/about",
    title: "About Trainer App | Built by a Lifter, for Coaches",
    description:
      "Learn about Trainer App — a simple, no-bloat online coaching platform built by a lifter to help personal trainers program workouts, manage clients, and grow their business.",
  },
  "/about-us": {
    canonical: "/about",
    title: "About Trainer App | Built by a Lifter, for Coaches",
    description:
      "Learn about Trainer App — a simple, no-bloat online coaching platform built by a lifter to help personal trainers program workouts, manage clients, and grow their business.",
  },
  "/how-it-works": {
    canonical: "/how-it-works",
    title: "How It Works | Trainer App Personal Trainer Software",
    description:
      "See how Trainer App works as personal trainer software and an online coaching platform: build custom training plans, deliver workouts to a client app, track progress, and manage payments from one trainer software platform.",
  },
  "/pricing": {
    canonical: "/pricing",
    title: "Pricing | Personal Trainer Software & Coaching App",
    description:
      "Simple, transparent pricing for personal trainer software. Pay per client with Trainer App's online coaching platform — start a free trial and scale as your coaching business grows.",
  },
  "/coaching": {
    canonical: "/coaching",
    title: "Start Your Free Trial | App for Personal Trainers",
    description:
      "Start coaching online with Trainer App, the app for personal trainers. Program workouts, manage clients on a personal training client app, collect payments, and grow your coaching business. Try it free.",
  },
  "/free-trial": {
    canonical: "/free-trial",
    title: "Free Trial | Personal Trainer Software & Client App",
    description:
      "Try Trainer App free — the personal trainer platform that pairs personal trainer software for coaches with a personal training client app for your clients. Build programs, track clients, and run your coaching business.",
  },
  "/support": {
    canonical: "/support",
    title: "Support | Trainer App",
    description:
      "Get help with Trainer App. Find answers, contact support, and learn how to get the most out of your online coaching platform.",
  },
  "/privacy-policy": {
    canonical: "/privacy-policy",
    title: "Privacy Policy | Trainer App",
    description:
      "Read the Trainer App privacy policy to understand how we collect, use, and protect your data.",
  },
};

const upsertMeta = (attr, key, content) => {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setCanonical = (href) => {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const FAQ_LD_ID = "route-faq-ld";

// FAQPage structured data must live on the page where the FAQ is actually
// visible (the About page), not sitewide. Add it on /about and remove it
// elsewhere so client-side navigation never leaves stale schema behind.
const syncFaqLd = (show) => {
  const existing = document.getElementById(FAQ_LD_ID);
  if (!show) {
    if (existing) existing.remove();
    return;
  }
  const data = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ_ITEMS.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  };
  const el = existing || document.createElement("script");
  el.id = FAQ_LD_ID;
  el.type = "application/ld+json";
  el.textContent = JSON.stringify(data);
  if (!existing) document.head.appendChild(el);
};

/**
 * Keeps the document title, canonical link, and social meta in sync with the
 * current route. Rendered once inside the Router.
 */
const RouteSeo = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const seo = ROUTE_SEO[pathname] || {
      ...DEFAULT_SEO,
      canonical: pathname || "/",
    };
    const url = `${SITE_URL}${seo.canonical || pathname || "/"}`;

    document.title = seo.title;
    setCanonical(url);
    upsertMeta("name", "description", seo.description);

    upsertMeta("property", "og:title", seo.title);
    upsertMeta("property", "og:description", seo.description);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", DEFAULT_IMAGE);

    upsertMeta("name", "twitter:title", seo.title);
    upsertMeta("name", "twitter:description", seo.description);
    upsertMeta("name", "twitter:image", DEFAULT_IMAGE);

    syncFaqLd(seo.canonical === "/about");
  }, [pathname]);

  return null;
};

export default RouteSeo;
